import DocumentHeader from '@/components/DocumentHeader';
import { Footer } from '@/components/Footer';
import { DiscoverTabs } from '@/components/Nav/DiscoverTabs';
import { SWS_HOST } from '@/constants';
import type { PrimaryCountryISOElseGlobal } from '@/constants/countries';
import {
  ROUTE_CREATE_CRITERIA,
  ROUTE_DISCOVER_INVESTING_IDEAS,
  ROUTE_DISCOVER_INVESTING_IDEAS_MARKET,
} from '@/constants/routes';
import { useFlag } from '@simplywallst/unleash';
import { useHrefLangMarkets } from '@/hooks/discover/useHrefLangMarkets';
import { useLocale } from '@/hooks/useLocale';
import ExploreIdeas from '@/pages/DiscoverHub/components/ExploreIdeas';
import { MarketIndustryFilter } from '@/pages/DiscoverHub/components/MarketIndustryFilter';
import { SwitchMarketButton } from '@/pages/DiscoverHub/components/SwitchMarketButton';
import { useMarketFilter } from '@/pages/DiscoverHub/hooks/useMarketFilter';
import {
  PageTranslation,
  usePageTranslation,
} from '@/pages/DiscoverHub/hooks/usePageTranslation';
import { getPath } from '@/router/utils';
import { getReadableCountry } from '@/utilities/markets';
import ScreenerOutline from '@simplywallst/ui-core/icons/ScreenerOutline';
import { Heading, Link } from '@simplywallst/ui-core/next';
import { useEffect, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import {
  pathToAction,
  redirect,
  type Action,
  type LocationState,
} from 'redux-first-router';
import { usePathname } from '@/hooks/location/usePathname';
import routesGenerator from '@/router/routesGenerator';

const routesMap = routesGenerator();

export interface Props {
  route: string;
  market?: PrimaryCountryISOElseGlobal;
}

const mapState = (state): Props => {
  const location = state.location as LocationState;
  return {
    route: location.type,
    market: location.payload.market,
  };
};

type BaseDiscoverHubProps = ReturnType<typeof mapState>;

const useMetaData = (market: PrimaryCountryISOElseGlobal, route: string) => {
  const t = usePageTranslation();
  const [country, countryDemonym] = useMemo(
    () => [
      getReadableCountry(market, 'label'),
      getReadableCountry(market, 'demonym'),
    ],
    [market]
  );
  const { hrefLangMarkets } = useHrefLangMarkets(route);

  const locale = useLocale();

  const baseMetaData = {
    // FIXME: temporarily blocking crawlers for non-English pages, until we are ready to release locale discover pages.
    allowCrawlers: locale === 'en', // Allow crawlers or hreflang tags will not work correctly
    hrefLangs: hrefLangMarkets,
  };

  if (market === 'global') {
    return {
      ...baseMetaData,
      title: t('meta.globalTitle'),
      description: t('meta.globalDescription'),
      pageTitle: t('pageTitle.global'),
      pageDescription: t('pageDescription.global'),
      canonical: `${SWS_HOST}${getPath({
        type: ROUTE_DISCOVER_INVESTING_IDEAS,
        payload: {},
      })}`,
    };
  }

  return {
    ...baseMetaData,
    title: t('meta.countryTitle', { countryDemonym }),
    description: t('meta.countryDescription', {
      country,
      countryDemonym,
    }),
    pageTitle: t('pageTitle.country', { countryDemonym }),
    pageDescription: t('pageDescription.country', {
      countryDemonym,
    }),
    canonical: `${SWS_HOST}${getPath({
      type: ROUTE_DISCOVER_INVESTING_IDEAS_MARKET,
      payload: { market },
    })}`,
  };
};

/**
 * Redirects user to the 'non-locale' version of the page if the locale-discover-page flag is not enabled.
 *
 * FIXME: This is a temporary guard that can be completely removed once the locale-discover-page flag is released to all.
 */
const useLocaleFlagRedirect = () => {
  const locale = useLocale();
  const localeFlag = useFlag('locale-discover-page');
  const pathname = usePathname();
  const dispatch = useDispatch();

  useEffect(() => {
    if (locale !== 'en' && !localeFlag.enabled) {
      const action = pathToAction(
        pathname.replace(`/${locale}`, ''),
        routesMap
      ) as Action;
      dispatch(
        redirect({
          ...action,
          payload: {
            ...action.payload,
            locale: 'en',
          },
        })
      );
    }
  }, [dispatch, locale, localeFlag.enabled, pathname]);
};

const BaseDiscoverHub = ({
  route,
  market = 'global',
}: BaseDiscoverHubProps) => {
  const meta = useMetaData(market, route);
  const { marketOptions, handleMarketChange } = useMarketFilter({ route });

  useLocaleFlagRedirect();

  return (
    <DiscoverTabs route={route} market={market}>
      <DocumentHeader
        title={meta.title}
        description={meta.description}
        allowCrawlers={meta.allowCrawlers}
      >
        {meta.allowCrawlers && <link rel="canonical" href={meta.canonical} />}
        {meta.hrefLangs && (
          <link
            rel="alternate"
            hrefLang="x-default"
            href={`${SWS_HOST}${getPath({
              // Technically incorrect global page for "logged in" users but it's fine
              type: ROUTE_DISCOVER_INVESTING_IDEAS,
              payload: {},
            })}`}
          />
        )}
        {meta.hrefLangs}
      </DocumentHeader>
      <header className="px-4 py-2 lg:px-0 lg:pb-1 lg:pt-3">
        <MarketIndustryFilter
          marketOptions={marketOptions}
          selectedMarket={market}
          onMarketChange={handleMarketChange}
          hideIndustryFilter={true}
          filtersAddon={market === 'global' && <SwitchMarketButton />}
        />
        <div className="grid gap-4 py-3 lg:py-5">
          <Heading size="mega">{meta.pageTitle}</Heading>
          {meta.pageDescription && (
            <div className="text-pretty text-xs text-softer md:max-w-[66%]">
              {meta.pageDescription}
            </div>
          )}
          <div className="lg:hidden">
            <div className="flex items-center justify-end gap-x0_5 text-xs">
              <PageTranslation i18nKey="tabs.stockScreenerCTA">
                Have your own idea? Try our
                <Link
                  className="text-current decoration-current"
                  href={getPath({
                    type: ROUTE_CREATE_CRITERIA,
                    payload: {},
                  })}
                >
                  Stock Screener
                </Link>
                <ScreenerOutline className="inline-block fill-soft" />
              </PageTranslation>
            </div>
          </div>
        </div>
      </header>
      <ExploreIdeas market={market} />
      <Footer />
    </DiscoverTabs>
  );
};

export default connect(mapState)(BaseDiscoverHub);
